import { CommonModule } from "@angular/common";
import { Component, inject, Input, OnInit, signal } from "@angular/core";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import {
  faCoins,
  faImage,
  faSpinner,
} from "@fortawesome/pro-light-svg-icons";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { formatCreditPrice } from "../credits.config";
import {
  CreditBundle,
  CreditsService,
  CreditType,
} from "../credits.service";

@Component({
  selector: "app-credit-purchase-modal",
  standalone: true,
  imports: [CommonModule, FontAwesomeModule],
  templateUrl: "./credit-purchase-modal.component.html",
  styleUrl: "./credit-purchase-modal.component.scss",
})
export class CreditPurchaseModalComponent implements OnInit {
  activeModal = inject(NgbActiveModal);
  private creditsService = inject(CreditsService);

  @Input() creditType: CreditType = "image";

  bundles = signal<CreditBundle[]>([]);
  selectedBundle = signal<CreditBundle | null>(null);
  purchasing = signal(false);
  error = signal<string | null>(null);

  coinsIcon = faCoins;
  imageIcon = faImage;
  spinnerIcon = faSpinner;

  ngOnInit() {
    // Load bundles from config (synchronous)
    const bundles = this.creditsService.getBundles(this.creditType);
    this.bundles.set(bundles);
  }

  selectBundle(bundle: CreditBundle) {
    this.selectedBundle.set(bundle);
    this.error.set(null);
  }

  formatPrice(bundle: CreditBundle): string {
    return formatCreditPrice(bundle.priceInCents, bundle.currency);
  }

  formatPricePerCredit(bundle: CreditBundle): string {
    const pricePerCredit = Math.round(bundle.priceInCents / bundle.credits);
    return formatCreditPrice(pricePerCredit, bundle.currency);
  }

  getCreditTypeLabel(): string {
    const config = this.creditsService.getCreditTypeConfig(this.creditType);
    return config?.label ?? this.creditType;
  }

  getCreditTypeIcon() {
    switch (this.creditType) {
      case "image":
        return this.imageIcon;
      default:
        return this.coinsIcon;
    }
  }

  purchase() {
    const bundle = this.selectedBundle();
    if (!bundle) return;

    this.purchasing.set(true);
    this.error.set(null);

    this.creditsService.purchaseCredits(bundle, this.creditType).subscribe({
      next: () => {
        // Redirect happens in the service
        this.purchasing.set(false);
      },
      error: (err) => {
        console.error("Purchase failed:", err);
        this.error.set(err.message || "Purchase failed. Please try again.");
        this.purchasing.set(false);
      },
    });
  }

  close() {
    this.activeModal.dismiss();
  }
}
